// @ts-nocheck
/* eslint-disable no-useless-concat */
import React, { Suspense, lazy, ComponentType, useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { AuthGuard, PrivateRoute } from "./Common";
import { ACCESS_TOKEN } from "../constants";
import CrossOriginLocalStorage from "../utils/CrossOriginLocalStorage";
import PubSub from "pubsub-js";
import { AuthService } from "../services/auth.service";
import { eventNames } from "../constants/eventNames";
import ApiService from "../services/api.service";
import { apiUrlConfig } from "../constants/apiUrlConfig";
import { jwtDecode } from "jwt-decode";
import "../components/web-crawler/style.css";
import { toast } from "react-toastify";
import { getCurrentUser, signout } from "../auth-ui/util/APIUtils";
import packageJson from "../../package.json";
import LoadingIndicator from "../auth-ui/common/LoadingIndicator";
import RouteTransitionLoader from "../components/common/RouteTransitionLoader";
import AutoLogout from "../services/autologout.service";

// Helper to properly lazy load components with default exports
// This ensures the component is a function, not an object (fixes prop type warning)
const lazyLoad = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) => lazy(importFn);

// Preload commonly used components to prevent loading flash when clicking tree nodes
const preloadComponent = (importFn: () => Promise<any>) => {
  // Start loading in the background after a short delay to not block initial load
  setTimeout(() => {
    importFn().catch(() => {
      // Silently ignore preload errors
    });
  }, 2000);
};

// Lazy load all route components for better performance
// Auth & Core
const Login = lazyLoad(() => import("../auth-ui/user/login/Login"));
const Signup = lazyLoad(() => import("../auth-ui/user/signup/Signup"));
const IFrame = lazyLoad(() => import("../auth-ui/common/IFrame"));
const Forgot = lazyLoad(() => import("../auth-ui/user/forgot/Forgot"));
const FailedContact = lazyLoad(() => import("../auth-ui/user/failed-contact/FailedContact"));
const Activate = lazyLoad(() => import("../auth-ui/user/activate/Activate"));
const Reset = lazyLoad(() => import("../auth-ui/user/reset/Reset"));
const AuthCallback = lazyLoad(() => import("../auth-ui/user/auth-callback/AuthCallback"));
const Profile = lazyLoad(() => import("../auth-ui/user/profile/Profile"));
const NotFound = lazyLoad(() => import("../auth-ui/common/NotFound"));
const Homes = lazyLoad(() => import("../auth-ui/home/home"));

// Flow & Components - These are frequently accessed from tree, so we preload them
const flowComponentImport = () => import("../components/component-flow/FlowComponent");
const FlowComponent = lazyLoad(flowComponentImport);
const WebTestLabLanding = lazyLoad(() => import("../components/web-test-lab-landing/WebTestLabLanding"));
const addWorkFlowImport = () => import("../components/flow/AddWorkFlow");
const AddWorkFlow = lazyLoad(addWorkFlowImport);
const addWorkConnectionImport = () => import("../components/connection/AddWorkConnection");
const AddFlowToGroup = lazyLoad(() => import("../components/flow-group/AddFlowToGroup"));
const RightBottom = lazyLoad(() => import("../components/component-flow/FlowActivityPanel"));

// Tables & Data
const Tables = lazyLoad(() => import("../components/table/Tables"));
const apiImport = () => import("../components/api/Api");
const Api = lazyLoad(apiImport);
const graphqlApiImport = () => import("../components/graphql-api/GraphqlApiPage");
const GraphqlApiPage = lazyLoad(graphqlApiImport);
const crawlerPageImport = () => import("../components/web-crawler/CrawlerPage");
const CrawlerPage = lazyLoad(crawlerPageImport);

// Licenses & Security
const AddNewLicense = lazyLoad(() => import("../components/licenses/add-new-license/AddNewLicense"));
const ManageLicenses = lazyLoad(() => import("../components/licenses/ManageLicenses"));
const SecurityConfig = lazyLoad(() => import("../components/licenses/security-config/SecurityConfig"));
const ChangPassword = lazyLoad(() => import("../components/licenses/change-password/ChangPassword"));

// Scripts - Frequently accessed from tree
const scriptImport = () => import("../components/script/Script");
const Script = lazyLoad(scriptImport);

// Kanban & Planning
const Home = lazyLoad(() => import("../components/kanboard/Home/Home"));
const BoardContainer = lazyLoad(() => import("../components/kanboard/Board/BoardContainer"));
const BoardMetricsDashboard = lazy(() =>
  import("../components/kanboard/BoardMetricsDashboard").then(m => ({ default: m.BoardMetricsDashboard }))
);

// Help & Documentation
const HelpComponent = lazyLoad(() => import("../components/component-help/HelpComponent"));

// Project & Users
const ProjectUserComponent = lazyLoad(() => import("../components/projectuser/ProjectUserComponent"));
const ProjectSettings = lazyLoad(() => import("../components/project-settings/ProjectSettings"));
const RuntimeVariableComponent = lazyLoad(() => import("../components/projectuser/RuntimeVariableComponent"));
const Userspage = lazyLoad(() => import("../components/component-user/user-page"));
const UserPage = lazyLoad(() => import("../components/component-user/user-page"));

// Audit
const AuditPage = lazyLoad(() => import("../components/component-audit/audit-page"));

// Connections - Using the already declared import function
const AddWorkConnection = lazyLoad(addWorkConnectionImport);

// Preload commonly used tree components after initial load
const preloadTreeComponents = () => {
  preloadComponent(flowComponentImport);
  preloadComponent(addWorkFlowImport);
  preloadComponent(addWorkConnectionImport);
  preloadComponent(apiImport);
  preloadComponent(scriptImport);
};

// Reporting
const ReportingCostSaving = lazy(() =>
  import("../components/component-flow/TestExecution/ReportingCostSaving").then(m => ({
    default: m.ReportingCostSaving
  }))
);
const ReportingTestCreation = lazy(() =>
  import("../components/component-flow/TestExecution/ReportingTestCreation").then(m => ({
    default: m.ReportingTestCreation
  }))
);
const ReportingResult = lazy(() =>
  import("../components/component-flow/TestExecution/ReportingResult").then(m => ({ default: m.ReportingResult }))
);

// Welcome & Policy
const WelcomeComponent = lazyLoad(() => import("../components/welcome/WelcomeComponent"));
const PrivacyPolicy = lazyLoad(() => import("../components/privacyPolicy/PrivacyPolicy"));

// Metrics
const Metrics = lazyLoad(() => import("../components/metrics/components/MetricsDashboard"));
const ExecutionReports = lazyLoad(() => import("../components/metrics/components/ExecutionReports"));
const PerfBudgets = lazyLoad(() => import("../components/metrics/components/PerfBudgets"));
const PerfFlakiness = lazyLoad(() => import("../components/metrics/components/PerfFlakiness"));
const Lighthouse = lazyLoad(() => import("../components/metrics/components/Lighthouse"));

// Test Case Management (TCM)
const TCMLanding = lazyLoad(() => import("../components/tcm/TCMLanding"));

// Project Management (PM) — deferred, component not yet available
// const PMLanding = lazyLoad(() => import("../components/pm/PMLanding"));
// const PMProjectDetail = lazyLoad(() => import("../components/pm/ProjectDetail"));

// Daemon Settings
const DaemonSettings = lazyLoad(() => import("../components/daemon-settings/DaemonSettings"));

// Software Requirements
const RequirementList = lazyLoad(() => import("../components/software-req/components/Requirements/RequirementList"));
const RequirementEditor = lazyLoad(() => import("../components/software-req/components/Requirements/RequirementEditor"));
const TechnicalSpecificationEditor = lazyLoad(
  () => import("../components/software-req/components/Dashboard/TechnicalSpecificationEditor")
);
const TechnicalSpecificationList = lazyLoad(
  () => import("../components/software-req/components/Dashboard/TechnicalSpecificationList")
);

// Agile Artifacts
const ArtifactForm = lazyLoad(() => import("../components/software-req/components/agileArtifacts/ArtifactForm"));
const ArtifactDetail = lazyLoad(() => import("../components/software-req/components/agileArtifacts/ArtifactDetail"));
const AgileArtifactsList = lazyLoad(
  () => import("../components/software-req/components/agileArtifacts/AgileArtifactsList")
);

// Risk Management
const RiskDashboard = lazyLoad(() => import("../components/software-req/components/RiskManagement/RiskDashboard"));
const RiskEditor = lazyLoad(() => import("../components/software-req/components/RiskManagement/RiskEditor"));

// Test Mapping
const TestMappingMatrix = lazyLoad(() => import("../components/software-req/components/TestMapping/TestMappingMatrix"));
const TestMappingEditor = lazyLoad(() => import("../components/software-req/components/TestMapping/TestMappingEditor"));
const OrphanTestsView = lazyLoad(() => import("../components/software-req/components/TestMapping/OrphanTestsView"));

// Scripts (Software Req)
const ScriptList = lazyLoad(() => import("../components/software-req/components/Scripts/ScriptList"));
const ScriptEditor = lazyLoad(() => import("../components/software-req/components/Scripts/ScriptEditor"));

// Workflow
const KanbanBoard = lazyLoad(() => import("../components/software-req/components/Workflow/KanbanBoard"));
const TimelineGantt = lazyLoad(() => import("../components/software-req/components/Workflow/TimelineGantt"));
const WorkflowEditor = lazyLoad(() => import("../components/software-req/components/Workflow/WorkflowEditor"));
const WorkflowList = lazyLoad(() => import("../components/software-req/components/Workflow/WorkflowList"));

// NLP
const NlpAnalysis = lazyLoad(() => import("../components/software-req/components/NLP/NlpAnalysis"));

// Architecture
const ArchitectureDiagramEditor = lazyLoad(
  () => import("../components/software-req/components/Dashboard/ArchitectureDiagramEditor")
);
const ArchitectureDiagramList = lazyLoad(
  () => import("../components/software-req/components/Dashboard/ArchitectureDiagramList")
);

// Collaboration
const CollaborationForm = lazyLoad(() => import("../components/software-req/components/collaboration/CollabortionFrom"));
const CollaborationList = lazyLoad(() => import("../components/software-req/components/collaboration/CollaborationList"));

// Survey
const SurveytemplateFrom = lazyLoad(
  () => import("../components/software-req/components/Surveytemplate/SurveytemplateFrom")
);
const SurverytemplateList = lazyLoad(
  () => import("../components/software-req/components/Surveytemplate/SurverytemplateList")
);

// Use Case
const UseCaseform = lazyLoad(() => import("../components/software-req/components/UseCase/UseCaseform"));
const UseCaseList = lazyLoad(() => import("../components/software-req/components/UseCase/UseCaseList"));

// Dashboard Config
const DashboardConfigList = lazyLoad(() => import("../components/software-req/components/Dashboard/DashboardConfigList"));
const DashboardConfigForm = lazyLoad(() => import("../components/software-req/components/Dashboard/DashboardConfigForm"));
const CoverageReport = lazyLoad(() => import("../components/software-req/components/Dashboard/CoverageReport"));

// Test Link
const TestLinkFrom = lazyLoad(() => import("../components/software-req/components/TestLink/TestLinkFrom"));
const TestLinkList = lazyLoad(() => import("../components/software-req/components/TestLink/TestLinkList"));

// Route change listener component using hooks (v6 compatible)
const RouteChangeListener = ({ onRouteChange }: { onRouteChange?: (location: any) => void }) => {
  const location = useLocation();

  useEffect(() => {
    if (onRouteChange) {
      onRouteChange(location);
    }
  }, [location, onRouteChange]);

  return null;
};

// Wrapper component to provide navigation to class component
interface AppRouterContentProps {
  dispatch: any;
  isAuthenticated: boolean;
  currentUser: any;
  isLoading: boolean;
  isRouteTransitioning: boolean;
  onLogin: (redirectUrl: string, accessToken: string) => void;
  onLogout: () => void;
  onRouteChange: (location: any) => void;
  AuthIframeRef: React.RefObject<HTMLIFrameElement>;
  onIframeLoaded: () => void;
}

// Compute once at module load. When the React app is itself running INSIDE
// the auth-ui-frame iframe (src="/iframe"), rendering ANOTHER auth-ui-frame
// would recurse — each level downloads the whole React bundle until the
// browser's iframe-depth limit (~32 in Chrome) stops it. The cross-storage
// helper only needs to bridge same-origin localStorage from the TOP document;
// nested levels share localStorage with the parent directly so they have no
// reason to embed it. try/catch covers the cross-origin window.top access
// case (defensive — the helper is same-origin in practice).
let __isInIframe = false;
try {
  __isInIframe = typeof window !== 'undefined' && window.top !== window;
} catch {
  __isInIframe = true;
}

const AppRouterContent = ({
  dispatch,
  isAuthenticated,
  currentUser,
  isLoading,
  isRouteTransitioning,
  onLogin,
  onLogout,
  onRouteChange,
  AuthIframeRef,
  onIframeLoaded
}: AppRouterContentProps) => {
  return (
    <div style={{ height: "100%" }}>
      {isLoading && <LoadingIndicator />}
      {isRouteTransitioning && <RouteTransitionLoader />}
      {!__isInIframe && (
        <iframe
          ref={AuthIframeRef}
          id="auth-ui-frame"
          src={"/iframe"}
          style={{ display: "none" }}
          width="0px"
          height="0px"
          title="auth-ui-frame"
          onLoad={onIframeLoaded}
        />
      )}

      <AuthGuard />
      <RouteChangeListener onRouteChange={onRouteChange} />
      <Suspense fallback={<LoadingIndicator />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login onLogin={onLogin} />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/iframe" element={<IFrame />} />
          <Route path="/forgot" element={<Forgot />} />
          <Route path="/failed-contact" element={<FailedContact />} />
          <Route path="/activate/:token" element={<Activate />} />
          <Route path="/reset-password/:token" element={<Reset />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/users/:username"
            element={
              <Profile
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
              />
            }
          />
          <Route path="/" element={<Homes />} />
          <Route path="/not-found" element={<NotFound />} />

          {/* Privacy Policy Route */}
          <Route
            path="/privacy-policy"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <PrivacyPolicy />
              </PrivateRoute>
            }
          />

          {/* Flow Routes */}
          <Route
            path="/welcome"
            element={
              <PrivateRoute>
                <WelcomeComponent />
              </PrivateRoute>
            }
          />
          <Route
            path="/components"
            element={
              <PrivateRoute>
                <WebTestLabLanding />
              </PrivateRoute>
            }
          />
          <Route
            path="/components/:id"
            element={
              <PrivateRoute>
                <FlowComponent />
              </PrivateRoute>
            }
          />

          {/* Test Case Management (TCM) Routes */}
          <Route
            path="/tcm"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TCMLanding />
              </PrivateRoute>
            }
          />
          <Route
            path="/tcm/*"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TCMLanding />
              </PrivateRoute>
            }
          />

          {/* Project Management (PM) Routes — deferred, component not yet available */}

          <Route
            path="/project-settings/:id"
            element={
              <PrivateRoute>
                <ProjectSettings />
              </PrivateRoute>
            }
          />
          <Route
            path="/uservariables"
            element={
              <PrivateRoute>
                <ProjectUserComponent />
              </PrivateRoute>
            }
          />
          <Route
            path="/uservariables/runtimeVariable/:id"
            element={
              <PrivateRoute>
                <RuntimeVariableComponent />
              </PrivateRoute>
            }
          />
          <Route
            path="/uservariables/:id"
            element={
              <PrivateRoute>
                <ProjectUserComponent />
              </PrivateRoute>
            }
          />
          <Route
            path="/work-flows"
            element={
              <PrivateRoute>
                <AddWorkFlow />
              </PrivateRoute>
            }
          />
          <Route
            path="/work-flows/:id"
            element={
              <PrivateRoute>
                <AddWorkFlow />
              </PrivateRoute>
            }
          />
          <Route
            path="/work-connections"
            element={
              <PrivateRoute>
                <AddWorkConnection />
              </PrivateRoute>
            }
          />
          <Route
            path="/work-connections/:id"
            element={
              <PrivateRoute>
                <AddWorkConnection />
              </PrivateRoute>
            }
          />
          <Route
            path="/tables"
            element={
              <PrivateRoute>
                <Tables />
              </PrivateRoute>
            }
          />
          <Route
            path="/tables/:topicTable"
            element={
              <PrivateRoute>
                <Tables />
              </PrivateRoute>
            }
          />

          {/* Test execution report - Keep old routes for backward compatibility */}
          <Route
            path="/reports/automationTestCreation"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ReportingTestCreation />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/automationTestCreation/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ReportingTestCreation />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/automationResult"
            element={
              <PrivateRoute>
                <ReportingResult />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/automationResult/:id"
            element={
              <PrivateRoute>
                <ReportingResult />
              </PrivateRoute>
            }
          />
          {/* Keep old routes for backward compatibility */}
          <Route
            path="/reports/automationCostSaving"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ReportingCostSaving />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/automationCostSaving/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ReportingCostSaving />
              </PrivateRoute>
            }
          />

          {/* Adding FlowGroup Routes */}
          <Route
            path="/flow-groups"
            element={
              <PrivateRoute>
                <AddFlowToGroup />
              </PrivateRoute>
            }
          />
          <Route
            path="/flow-groups/:id"
            element={
              <PrivateRoute>
                <AddFlowToGroup />
              </PrivateRoute>
            }
          />
          <Route
            path="/group/flow/assign"
            element={
              <PrivateRoute>
                <AddFlowToGroup />
              </PrivateRoute>
            }
          />
          <Route
            path="/group/flow/assign/:id"
            element={
              <PrivateRoute>
                <AddFlowToGroup />
              </PrivateRoute>
            }
          />

          {/* JavaScriptEditor Routes */}
          <Route
            path="/scripts"
            element={
              <PrivateRoute>
                <Script />
              </PrivateRoute>
            }
          />
          <Route
            path="/scripts/:id"
            element={
              <PrivateRoute>
                <Script />
              </PrivateRoute>
            }
          />
          <Route
            path="/api"
            element={
              <PrivateRoute>
                <Api />
              </PrivateRoute>
            }
          />
          <Route
            path="/api/:id"
            element={
              <PrivateRoute>
                <Api />
              </PrivateRoute>
            }
          />

          <Route
            path="/graphql/:id"
            element={
              <PrivateRoute>
                <GraphqlApiPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/crawlers/:id"
            element={
              <PrivateRoute>
                <CrawlerPage />
              </PrivateRoute>
            }
          />

          {/* Controller Routes */}
          <Route path="/controllers" element={<Navigate to="/controllers/add-license" replace />} />
          <Route
            path="/controllers/add-license"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <AddNewLicense />
              </PrivateRoute>
            }
          />
          <Route
            path="/controllers/licenses"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ManageLicenses />
              </PrivateRoute>
            }
          />
          <Route
            path="/controllers/licenses/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ManageLicenses />
              </PrivateRoute>
            }
          />
          {/* Execution Reports under Metrics */}
          <Route
            path="/metrics/execution-reports"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ExecutionReports />
              </PrivateRoute>
            }
          />
          <Route
            path="/metrics/execution-reports/:reportId"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ExecutionReports />
              </PrivateRoute>
            }
          />
          {/* Phase B1b — Perf-budget editor (per-project). Sibling of
              execution-reports so navigation stays inside the same nav
              context and the two screens share styles + palette. */}
          <Route
            path="/metrics/perf-budgets"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <PerfBudgets />
              </PrivateRoute>
            }
          />
          {/* Phase B7 — Flaky-Perf dashboard: per-URL breach leaderboard
              across the last N days. Read-only aggregation of the same
              NavigationPerf data the per-run detail view shows. */}
          <Route
            path="/metrics/perf-flakiness"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <PerfFlakiness />
              </PrivateRoute>
            }
          />
          {/* Phase C1 — Lighthouse audits (on-demand, sidecar-backed). */}
          <Route
            path="/metrics/lighthouse"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <Lighthouse />
              </PrivateRoute>
            }
          />
          <Route
            path="/metrics"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <Metrics />
              </PrivateRoute>
            }
          />
          <Route
            path="/metrics/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <Metrics />
              </PrivateRoute>
            }
          />
          {/* Test Creation under Metrics */}
          <Route
            path="/metrics/test-creation"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ReportingTestCreation />
              </PrivateRoute>
            }
          />
          <Route
            path="/metrics/test-creation/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ReportingTestCreation />
              </PrivateRoute>
            }
          />
          {/* Cost Saving under Metrics */}
          <Route
            path="/metrics/cost-saving"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ReportingCostSaving />
              </PrivateRoute>
            }
          />
          <Route
            path="/metrics/cost-saving/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ReportingCostSaving />
              </PrivateRoute>
            }
          />
          <Route
            path="/controllers/security-config"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <SecurityConfig />
              </PrivateRoute>
            }
          />
          <Route
            path="/controllers/change-password"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ChangPassword />
              </PrivateRoute>
            }
          />
          <Route
            path="/controllers/daemon-settings"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <DaemonSettings />
              </PrivateRoute>
            }
          />

          {/* Kanboard loading Routes */}
          <Route
            path="/kanboard"
            element={
              <PrivateRoute extras={{ withoutTree: false }}>
                <Home />
              </PrivateRoute>
            }
          />
          <Route
            path="/b/:boardId/:boardTitle?"
            element={
              <PrivateRoute extras={{ withoutTree: false }}>
                <BoardContainer />
              </PrivateRoute>
            }
          />
          {/* Board Metrics Dashboard - Reports */}
          <Route
            path="/board-metrics/:boardId"
            element={
              <PrivateRoute extras={{ withoutTree: false }}>
                <BoardMetricsDashboard />
              </PrivateRoute>
            }
          />

          {/* RightBottomNewWindow - Public routes */}
          <Route path="/rightBottomWindow/:id" element={<RightBottom />} />
          <Route path="/apiWindow/:id" element={<Api />} />

          {/* Requirements */}
          <Route
            path="/requirements"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <RequirementList />
              </PrivateRoute>
            }
          />
          <Route
            path="/requirements/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <RequirementEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/requirements/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <RequirementEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/requirements/:reqId/spec/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TechnicalSpecificationEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/requirements/:reqId/spec/:specId"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TechnicalSpecificationEditor />
              </PrivateRoute>
            }
          />

          {/* Agile Artifacts */}
          <Route
            path="/artifacts"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <AgileArtifactsList />
              </PrivateRoute>
            }
          />
          <Route
            path="/artifacts/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ArtifactForm />
              </PrivateRoute>
            }
          />
          <Route
            path="/artifacts/edit/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ArtifactForm />
              </PrivateRoute>
            }
          />
          <Route
            path="/artifacts/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ArtifactDetail />
              </PrivateRoute>
            }
          />

          {/* Risk */}
          <Route
            path="/risk-dashboard"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <RiskDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/risks/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <RiskEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/risks/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <RiskEditor />
              </PrivateRoute>
            }
          />

          {/* Test */}
          <Route
            path="/test-mapping"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TestMappingMatrix />
              </PrivateRoute>
            }
          />
          <Route
            path="/test-mapping/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TestMappingEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/scriptsdata"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ScriptList />
              </PrivateRoute>
            }
          />
          <Route
            path="/scriptsdata/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ScriptEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/scriptsdata/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ScriptEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/orphan-tests"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <OrphanTestsView />
              </PrivateRoute>
            }
          />

          {/* Boards / Planning */}
          <Route
            path="/timeline"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TimelineGantt />
              </PrivateRoute>
            }
          />

          {/* Workflow */}
          <Route
            path="/workflow"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <WorkflowList />
              </PrivateRoute>
            }
          />
          <Route
            path="/workflow/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <WorkflowEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/workflow/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <WorkflowEditor />
              </PrivateRoute>
            }
          />

          {/* NLP */}
          <Route
            path="/nlp/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <NlpAnalysis />
              </PrivateRoute>
            }
          />

          {/* Architecture */}
          <Route
            path="/diagram"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ArchitectureDiagramList />
              </PrivateRoute>
            }
          />
          <Route
            path="/architecture-diagram/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ArchitectureDiagramEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/architecture-diagram/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <ArchitectureDiagramEditor />
              </PrivateRoute>
            }
          />

          {/* Collaboration */}
          <Route
            path="/collaborations"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <CollaborationList />
              </PrivateRoute>
            }
          />
          <Route
            path="/collaborations/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <CollaborationForm />
              </PrivateRoute>
            }
          />
          <Route
            path="/collaborations/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <CollaborationForm />
              </PrivateRoute>
            }
          />

          {/* Survey */}
          <Route
            path="/surveytemplates"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <SurverytemplateList />
              </PrivateRoute>
            }
          />
          <Route
            path="/surveytemplates/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <SurveytemplateFrom />
              </PrivateRoute>
            }
          />
          <Route
            path="/surveytemplates/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <SurveytemplateFrom />
              </PrivateRoute>
            }
          />

          {/* Use Case */}
          <Route
            path="/usecases"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <UseCaseList />
              </PrivateRoute>
            }
          />
          <Route
            path="/usecases/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <UseCaseform />
              </PrivateRoute>
            }
          />
          <Route
            path="/usecases/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <UseCaseform />
              </PrivateRoute>
            }
          />

          {/* Dashboard Config */}
          <Route
            path="/dashboard-configs"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <DashboardConfigList />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard-configs/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <DashboardConfigForm />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard-configs/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <DashboardConfigForm />
              </PrivateRoute>
            }
          />

          {/* Technical Specifications */}
          <Route
            path="/spec"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TechnicalSpecificationList />
              </PrivateRoute>
            }
          />
          <Route
            path="/spec/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TechnicalSpecificationEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/spec/:specId"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TechnicalSpecificationEditor />
              </PrivateRoute>
            }
          />

          {/* Test Links */}
          <Route
            path="/testlinks"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TestLinkList />
              </PrivateRoute>
            }
          />
          <Route
            path="/testlinks/new"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TestLinkFrom />
              </PrivateRoute>
            }
          />
          <Route
            path="/testlinks/:id"
            element={
              <PrivateRoute extras={{ withoutTree: true }}>
                <TestLinkFrom />
              </PrivateRoute>
            }
          />

          {/* Help Route */}
          <Route
            path="/help"
            element={
              <PrivateRoute extras={{ withoutTree: false }}>
                <HelpComponent />
              </PrivateRoute>
            }
          />

          {/* Users */}
          <Route
            path="/user"
            element={
              <PrivateRoute extras={{ withoutTree: false }}>
                <UserPage />
              </PrivateRoute>
            }
          />

          {/* Audit */}
          <Route
            path="/audit"
            element={
              <PrivateRoute extras={{ withoutTree: false }}>
                <AuditPage />
              </PrivateRoute>
            }
          />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/not-found" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
};

class AppRouter extends React.Component {
  private authIframe: HTMLIFrameElement | null = null;
  private AuthIframeRef = React.createRef<HTMLIFrameElement>();
  private crossStorage: CrossOriginLocalStorage | null = null;
  private api: any;
  private _mounted: boolean = false;

  constructor(props) {
    super(props);
    this.handleLogout = this.handleLogout.bind(this);
    this.loadCurrentUser = this.loadCurrentUser.bind(this);
    this.handleLogin = this.handleLogin.bind(this);

    this.state = {
      authenticated: false,
      currentUser: null,
      loading: true,
      crawlerUrl: "",
      isAuthenticated: false,
      isLoading: false,
      isRouteTransitioning: false
    };
    this.api = ApiService.getInstance();
  }

  componentDidMount() {
    // Prevent multiple mounts
    if (this._mounted) {
      return;
    }
    this._mounted = true;

    let versionElement = document.getElementsByName("version");
    if (versionElement && versionElement[0]) {
      versionElement[0].setAttribute("version", packageJson.version);
    }
    this.loadCurrentUser();
    // The auth-ui-frame iframe is only rendered by the top-level document (see
    // __isInIframe guard above). In nested React instances the ref is null,
    // so skip the CrossOriginLocalStorage init — the constructor reads
    // iframe.contentWindow which would throw on null. All callers use
    // `this.crossStorage?.method()` safe-navigation so a null instance is a
    // safe no-op.
    if (this.AuthIframeRef.current) {
      this.crossStorage = new CrossOriginLocalStorage(this.AuthIframeRef.current);
    }
    window.onmessage = event => {
      try {
        if (typeof event.data === "string") {
          // Try to parse as JSON - if it fails, it's not a valid auth message
          try {
            const parsedData = JSON.parse(event.data);
            this.onAuthIframeMessage(parsedData, event);
          } catch (parseError) {
            // Ignore non-JSON messages (could be JWT tokens or other data)
          }
        }
      } catch (error) {
        // Error handling window message
      }
    };

    PubSub.subscribe(eventNames.checkAuthToken, () => {
      this.crossStorage?.getData(ACCESS_TOKEN);
    });
    PubSub.subscribe(eventNames.setAuthToken, (event, data) => {
      // Send only the access_token string to the iframe, not the full response object.
      // The iframe stores whatever it receives, and sending the full object causes
      // the accessToken key to hold a JSON object instead of a raw JWT string,
      // which corrupts the token on the next iframe read-back.
      const tokenToSync = data?.access_token || data;
      this.crossStorage?.setData(ACCESS_TOKEN, tokenToSync);
      this.api.startRefreshTokenTimer();
      // Setup activity listeners to keep session alive while user is active
      this.api.setupActivityListeners();
    });

    // GUIDED-RECORD global subscriber (P7).
    //
    // The extension dispatches AUTO_CREATE_NEW_COMPONENT for every URL change
    // during a Guided record session. IFrame.tsx republishes it as
    // PubSub `createAutoComponent`. The per-page editors (FlowActivityPanel /
    // ComponentActivityPanel) already subscribe — but they're only mounted on
    // /work-flows/:id or /components/:id, which the user typically ISN'T on
    // while recording (they're on CrawlerPage or another tab entirely).
    //
    // The dispatch payload carries `data.flowId` ONLY in guided-record mode
    // (added in popup.js when ACTIVE_RECORDING_FLOW is set). When present we
    // own creation here: POST /components, then PATCH /flows/{flowId} with
    // {add:{componentId}} to append. The mounted-editor subscribers skip out
    // when `flowId` is present so we don't double-create.
    PubSub.subscribe(eventNames.createAutoComponent, async (_msg: string, payload: any) => {
      // Two publishers exist with different payload shapes:
      //   - component-flow/IFrame.tsx publishes `{ data: element, tabUrl }`
      //   - component-connection/IFrame.tsx publishes `element` directly
      // Normalize so we accept either.
      const element = payload?.data ?? payload;
      const flowId = element?.flowId;
      if (!flowId) {
        return; // Not a guided-record dispatch — let the editor subscribers handle it.
      }
      // Verify against localStorage in case a stale dispatch arrives after Stop.
      let context: { flowId: string; flowName: string; groupId: string } | null = null;
      try {
        const raw = localStorage.getItem("active-recording-flow");
        if (raw) context = JSON.parse(raw);
      } catch {
        /* ignore */
      }
      if (!context || context.flowId !== flowId) {
        console.warn(
          "[AppRouters] createAutoComponent with flowId but no matching active-recording-flow — ignoring stale dispatch",
        );
        return;
      }
      const { groupId, flowName } = context;
      const compName: string = (element.name || "").replace(/\s/g, "");
      const compUrl: string = element.link || "";
      if (!compName || !compUrl || !groupId) {
        console.warn("[AppRouters] guided-record dispatch missing fields", { compName, compUrl, groupId });
        return;
      }

      try {
        const api = ApiService.getInstance();
        // 1) Create the Component in the project's Components group.
        const createRes = await api.post(apiUrlConfig.components(), {
          name: compName,
          link: compUrl,
          groupId,
        });
        if (!createRes || !createRes.success || !createRes.payload?.id) {
          const desc = (createRes && (createRes as any).error?.description) || "create failed";
          toast.error(`Recording: could not create "${compName}" — ${desc}`);
          return;
        }
        const newCompId = createRes.payload.id as string;

        // 2) Append to the Flow via PATCH /flows/{flowId}.
        const addRes = await api.patch(apiUrlConfig.flows(flowId), {
          add: { componentId: newCompId },
        });
        if (!addRes || !addRes.success) {
          toast.warning(`"${compName}" was created but couldn't be added to flow "${flowName}".`);
          return;
        }
        toast.success(`Captured "${compName}" → flow "${flowName}"`);

        // 3) Refresh the tree so the new Component (and updated Flow membership)
        //    becomes visible without a manual refresh — same mechanism the BFS
        //    crawler import uses (see CrawlerPage importSelected).
        PubSub.publish(eventNames.refreshTree, { forceReloadChildren: true });
      } catch (err: any) {
        console.error("[AppRouters] guided-record create+append failed:", err);
        toast.error(`Recording error: ${err?.message || "Unknown error"}`);
      }
    });
  }

  handleRouteChange = (location: any) => {
    // Don't set isRouteTransitioning to true anymore - it causes flickering
    // The Suspense fallback will handle loading states for lazy components
  };

  loadCurrentUser() {
    this.setState({
      isLoading: true
    });
    getCurrentUser()
      .then(response => {
        this.props.dispatch({ type: "SET_USER", payload: response });
        this.setState({
          currentUser: response,
          isAuthenticated: true,
          isLoading: false
        });
        // Preload commonly used tree components after successful authentication
        preloadTreeComponents();
        
        // Start auto-logout monitoring after successful authentication
        const autoLogoutInstance = AutoLogout.getInstance();
        if (autoLogoutInstance) {
          autoLogoutInstance.start();
        } else {
          new AutoLogout();
        }
      })
      .catch(error => {
        this.setState({
          isLoading: false
        });
      });
  }

  handleLogout(redirectTo = "/", notificationType = "success", description = "You're successfully logged out.") {
    // Stop auto-logout monitoring before logging out
    const autoLogoutInstance = AutoLogout.getInstance();
    if (autoLogoutInstance) {
      autoLogoutInstance.stop();
    }

    signout().then(res => {
      localStorage.removeItem(ACCESS_TOKEN);

      this.setState({
        currentUser: null,
        isAuthenticated: false
      });

      // Use window.location for navigation from class component
      window.location.href = "/login";

      toast[notificationType](description);
    });
  }

  handleLogin(redirectUrl: string, accessToken: string) {
    toast.success("You're successfully logged in.");
    if (redirectUrl) {
      window.location.href =
        redirectUrl + `${redirectUrl.includes("?") ? "&" : "?"}accessToken=` + accessToken + "&fromLogin=true";
    } else {
      window.location.href =
        `${process.env.REACT_APP_AUTH_UI_URL}/welcome` + "?accessToken=" + accessToken + "&fromLogin=true";
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    PubSub.unsubscribe(eventNames.checkAuthToken);
    PubSub.unsubscribe(eventNames.setAuthToken);
  }

  onAuthIframeMessage(event) {
    if (event.method === "storage#get") {
      if (!event.data) {
        // No token data from iframe — normal for direct login (iframe has separate localStorage)
      } else {
        try {
          // The iframe may return either:
          // 1. A JSON object string: '{"access_token":"eyJ...","refresh_token":"eyJ..."}'
          //    (legacy format from login or old token sync)
          // 2. A raw JWT string: 'eyJ...' (new format after token sync fix)
          let authData;
          try {
            authData = JSON.parse(event.data);
          } catch {
            // Not valid JSON — treat as raw JWT string
            authData = null;
          }

          if (authData && authData.refresh_token) {
            // Legacy JSON format with both tokens — use directly
            this.props.dispatch({ type: "SET_USER", payload: JSON.parse(jwtDecode(authData.access_token).sub) });
            AuthService.token = authData.access_token;
            AuthService.refreshToken = authData.refresh_token;
            AuthService.clearForceLogoutFlag();
            this.api.startRefreshTokenTimer();
            this.api.setupActivityListeners();

            const autoLogoutInstance = AutoLogout.getInstance();
            if (autoLogoutInstance) {
              autoLogoutInstance.start();
            } else {
              new AutoLogout();
            }

            this.setState({
              crawlerUrl: `${process.env.REACT_APP_CRAWLER_URL}?access_token=${authData.refresh_token}`
            });
          } else if (typeof event.data === 'string' && event.data.startsWith('eyJ')) {
            // Raw JWT string from iframe — the main app already has
            // both tokens in localStorage from the refresh handler.
            // Just ensure the refresh timer is running.
            this.api.startRefreshTokenTimer();
          }
        } catch (e) {
          console.error('[AppRouters] Failed to process iframe auth data:', e);
        }
      }
    }
  }

  onIframeLoaded = () => {
    this.crossStorage?.getData(ACCESS_TOKEN);
  };

  render() {
    return (
      <Router>
        <AppRouterContent
          dispatch={this.props.dispatch}
          isAuthenticated={this.state.isAuthenticated}
          currentUser={this.state.currentUser}
          isLoading={this.state.isLoading}
          isRouteTransitioning={this.state.isRouteTransitioning}
          onLogin={this.handleLogin}
          onLogout={this.handleLogout}
          onRouteChange={this.handleRouteChange}
          AuthIframeRef={this.AuthIframeRef}
          onIframeLoaded={this.onIframeLoaded}
        />
      </Router>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {};
};

export default connect(mapStateToProps)(AppRouter);
